<script setup>
import { ref, watch, reactive, onMounted, onUnmounted, computed } from "vue";
import {
  doc,
  collection,
  query,
  orderBy,
  limit,
  onSnapshot,
} from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import { db, firebaseFunctions } from "@/firebase";
import Card from "primevue/card";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import { SwipeModal } from "@takuma-ru/vue-swipe-modal";
import CountdownBid from "./CountdownBid.vue";
import InputSwitch from "primevue/inputswitch";
import PriceDisplay from "@/views/auction/components/DisplayPrice.vue";

// Cloud Function reference
const placeBidFn = httpsCallable(firebaseFunctions, "placeBid");

const props = defineProps({
  item: {
    type: Object,
    required: true,
    default: () => ({
      id: "",
      latestBid: 0,
      startbid: 0,
      enddate: { seconds: 0 },
    }),
  },
  user: {
    type: Object,
    required: true,
    default: () => ({ id: "", name: "", firstName: "", lastName: "" }),
  },
});
const emit = defineEmits(["modalStatusChanged"]);

const store = useStore();
const router = useRouter();

const isKiosk = computed(() => localStorage.getItem("deviceKiosk") === "true");
const showKioskSignOutPrompt = ref(false);

const kioskSwitchUser = async () => {
  const tableId = props.user?.assignedTable || "";
  await store.dispatch("logOut");
  if (tableId) {
    router.push({ path: "/", query: { tableId } });
  } else {
    router.push("/");
  }
};

const isBidding = ref(false);
const bidAmount = ref("");
const errorMessage = ref("");

const isOpen = ref(false);
const postAnonymously = ref(false);
const reactiveItem = reactive({ ...props.item });
const reactiveUser = reactive({ ...props.user });

const showHighestBidderPopup = ref(false);
const bidIncrement = computed(() => {
  if (reactiveItem.latestBid < 10000) {
    return 500;
  } else if (reactiveItem.latestBid < 50000) {
    return 1000;
  } else if (reactiveItem.latestBid < 100000) {
    return 2000;
  } else {
    return 5000;
  }
});
let unsubscribe;

const subscribeToBids = (itemDocRef) => {
  const bidsCollectionRef = collection(itemDocRef, "bids");

  const highestBidQuery = query(
    bidsCollectionRef,
    orderBy("maxBid", "desc"),
    orderBy("timestamp", "desc"),
    limit(1)
  );

  return onSnapshot(highestBidQuery, (querySnapshot) => {
    if (!querySnapshot.empty) {
      const highestBidDoc = querySnapshot.docs[0];
      const highestMaxBid = highestBidDoc.data().maxBid;
      reactiveItem.highestMaxBid = highestMaxBid;
    }
  });
};

const subscribeToItem = (itemId) => {
  const itemDocRef = doc(db, "auctionItems", itemId);

  unsubscribe = onSnapshot(itemDocRef, async (docSnap) => {
    if (docSnap.exists()) {
      const itemData = docSnap.data();

      Object.assign(reactiveItem, {
        latestBid: Number(itemData.latestBid),
        latestBidder: itemData.latestBidder,
        latestBidderID: itemData.latestBidderID,
        description: itemData.description,
        enddate: itemData.enddate,
        startbid: Number(itemData.startbid),
        currency: itemData.currency,
        hasBuyNowBid: itemData.hasBuyNowBid,
        buyNowPrice: itemData.buyNowPrice,
        isBuyNowEnabled: itemData.isBuyNowEnabled,
        highestMaxBid: reactiveItem.highestMaxBid,
      });

      subscribeToBids(itemDocRef);
    }
  });
};

const canShowBuyNow = computed(() => {
  const highestMaxBidExists = typeof reactiveItem.highestMaxBid === "number";

  return (
    reactiveItem.isBuyNowEnabled === true &&
    !reactiveItem.hasBuyNowBid &&
    (!highestMaxBidExists ||
      reactiveItem.highestMaxBid < reactiveItem.buyNowPrice)
  );
});

const formatBidAmount = (value) => {
  if (!value) return "";
  return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

const parseBidAmount = (value) => {
  return value.toString().replace(/,/g, "");
};

watch(bidAmount, (newVal) => {
  const parsedValue = parseBidAmount(newVal);
  bidAmount.value = formatBidAmount(parsedValue);
});

watch(
  () => props.item,
  (newItem) => {
    Object.assign(reactiveItem, newItem);
    if (unsubscribe) unsubscribe();
    subscribeToItem(newItem.id);
  },
  { immediate: true }
);

watch(
  () => props.user,
  (newUser) => {
    Object.assign(reactiveUser, newUser);
  },
  { immediate: true }
);

watch(isOpen, (newVal) => {
  emit("modalStatusChanged", newVal);
});

// ---------------------------------------------------------------------------
// placeBid — calls the server-side Cloud Function
// ---------------------------------------------------------------------------
const placeBid = async (bidtype) => {
  try {
    if (!isBidding.value) {
      isBidding.value = true;
      errorMessage.value = "";

      let userPostBid = parseFloat(parseBidAmount(bidAmount.value));
      if (bidtype === "BuyNow") {
        userPostBid = props.item.buyNowPrice;
      }

      if (isNaN(userPostBid) || userPostBid <= 0) {
        throw new Error("Please enter a valid number.");
      }

      // Support admin bidding on behalf of another user
      const customUser = store.getters["customUser"];
      let onBehalfOfUserId = null;
      if (customUser && customUser.id !== reactiveUser.id) {
        onBehalfOfUserId = reactiveUser.id;
      }

      const result = await placeBidFn({
        itemId: props.item.id,
        bidAmount: userPostBid,
        bidType: bidtype,
        anonymous: postAnonymously.value,
        onBehalfOfUserId,
        userId: props.user.id || store.getters["userDocId"],
      });

      const data = result.data;

      // Update local reactive state from server response
      reactiveItem.latestBid = data.newLatestBid;
      reactiveItem.latestBidder = data.latestBidder;
      reactiveItem.latestBidderID = data.latestBidderID;

      // Show congratulations popup if user is the highest bidder
      if (data.isHighestBidder) {
        showHighestBidderPopup.value = true;
      }

      // In kiosk mode, prompt user to sign out after placing a bid
      if (isKiosk.value && !showHighestBidderPopup.value) {
        showKioskSignOutPrompt.value = true;
      }

      bidAmount.value = "";
      isOpen.value = false;
    }
  } catch (error) {
    // Cloud Function errors come wrapped in a FirebaseError with .message
    const msg = error.details || error.message || "An error occurred.";
    errorMessage.value = msg;
  } finally {
    isBidding.value = false;
  }
};

const hasAuctionEnded = computed(() => {
  const now = new Date();
  const ended = now > new Date(reactiveItem.enddate.seconds * 1000);
  const hasBuyNowBid = reactiveItem.hasBuyNowBid ?? false;

  return ended || hasBuyNowBid;
});

onMounted(() => {
  if (props.item && props.item.id) {
    subscribeToItem(props.item.id);
  }
  // Pre-warm the Cloud Function to avoid cold start delay on first bid
  placeBidFn({ warmup: true }).catch(() => {});
});

onUnmounted(() => {
  if (unsubscribe) {
    unsubscribe();
  }
});
</script>

<template>
  <!-- Mobile Bid -->
  <Card style="overflow: hidden">
    <template #content>
      <div class="flex-wrap gap-2">
        <div class="flex flex-column">
          <div class="price-display">
            <span v-if="item.latestBid"
              >Current Bid:
              <strong
                ><PriceDisplay
                  :price="item.latestBid"
                  :currency="item.currency" /></strong
            ></span>
            <span v-else
              >Starting Bid:
              <PriceDisplay :price="item.startbid" :currency="item.currency"
            /></span>
          </div>

          <div v-if="reactiveItem.latestBidderID" class="flex flex-column mt-2">
            <p class="text-left bidding-info">
              <span
                class="p-chip-icon pi pi-star-fill"
                data-pc-section="icon"
                style="color: #f0ad4e"
              ></span>
              Highest bidder: {{ reactiveItem.latestBidder }}
            </p>
          </div>

          <!-- Bidding Form -->

          <SwipeModal
            v-model="isOpen"
            snapPoint="auto"
            style="background-color: #ffffff"
          >
            <div
              class="absolute top-0 right-0 font-bold flex align-items-center justify-content-center w-3rem h-3rem border-round"
            >
              <Button
                icon="pi pi-times"
                severity="contrast"
                text
                aria-label="Cancel"
                @click="isOpen = false"
              />
            </div>
            <div class="grid m-2">
              <div class="col-7 text-left">
                <p style="color: #333; font-size: 0.8rem">
                  Next minimum bid:
                  <strong>
                    <PriceDisplay
                      :price="
                        reactiveItem.latestBid + bidIncrement ||
                        reactiveItem.startbid
                      "
                      :currency="item.currency"
                    />
                  </strong>
                </p>
              </div>
              <div class="col-5 text-right">
                <CountdownBid
                  :endDateTimestamp="reactiveItem.enddate"
                  class="countdowntext"
                />
              </div>
            </div>

            <div class="flex m-2">
              <small class="mr-2" style="color: var(--gray-800)"
                >Place bid anonymously?</small
              >
              <InputSwitch v-model="postAnonymously" />
            </div>

            <div class="flex m-2">
              <div class="flex flex-grow-1">
                <InputText
                  v-model="bidAmount"
                  placeholder="Your bid"
                  class=""
                  style="width: 100%"
                  type="text"
                />
              </div>
              <div class="flex">
                <Button
                  label="Auto Bid"
                  class="item-btn small btn-darkblue"
                  rounded
                  @click="placeBid('AutoBid')"
                />
              </div>
              <div class="flex">
                <Button
                  label="Direct Bid"
                  class="item-btn small btn-lightblue"
                  rounded
                  @click="placeBid('DirectBid')"
                />
              </div>
            </div>
            <div
              v-if="canShowBuyNow"
              class="buynowitem pr-3 pl-3"
              style="color: #000000; padding-top: 0.2rem"
            >
              <Divider />
              <div class="flex m-2 flex justify-content-center flex-wrap">
                <div class="flex justify-content-center">
                  <span class="pt-1 pr-1">
                    Or Buy Now for
                    <PriceDisplay
                      :price="reactiveItem.buyNowPrice"
                      :currency="reactiveItem.currency"
                    />
                  </span>
                  <Button
                    label="Buy Now"
                    class="item-btn small btn-gold"
                    rounded
                    @click="placeBid('BuyNow')"
                  />
                </div>
              </div>
            </div>
            <div class="grid grid-nogutter">
              <div class="col-12">
                <p v-if="errorMessage" class="text-red-500 text-sm pb-0 mb-0">
                  {{ errorMessage }}
                </p>
              </div>
            </div>
            <div class="flex justify-content-start align-content-center p-2">
              <div class="flex align-items-center justify-content-start">
                <i
                  class="pi pi-info-circle"
                  style="font-size: 1rem; color: var(--gray-800)"
                ></i>
              </div>
              <div
                class="flex align-items-center justify-content-start flex-grow-1"
              >
                <small
                  class="m-3"
                  style="
                    color: var(--gray-800);
                    font-size: 1rem;
                    text-align: left;
                  "
                >
                  <strong>Auto bid</strong> automatically place new bids for
                  you, up to your maximum amount.<br />
                  <strong>Direct bid</strong> are posted immediately without
                  waiting for other bids to be raised.</small
                >
              </div>
            </div>
          </SwipeModal>
          <!-- Bidding Form End-->

          <div class="inline-flex">
            <div class="hidden md:block">
              <!-- Show on Desktop-->
              <div v-if="!isBidding && !hasAuctionEnded">
                <div class="grid nested-grid">
                  <div class="col-6 text-left">
                    <p style="color: #333; font-size: 0.8rem">
                      Next minimum bid:
                      <strong>
                        <PriceDisplay
                          :price="
                            reactiveItem.latestBid + bidIncrement ||
                            reactiveItem.startbid
                          "
                          :currency="item.currency"
                        />
                      </strong>
                    </p>
                  </div>
                  <div class="col-6 text-right">
                    <CountdownBid
                      :endDateTimestamp="reactiveItem.enddate"
                      class="countdowntext"
                    />
                  </div>
                  <div class="flex px-2">
                    <small class="mr-2" style="color: var(--gray-800)"
                      >Place bid anonymously?</small
                    >
                    <InputSwitch v-model="postAnonymously" />
                  </div>
                  <div class="flex m-2">
                    <div class="flex flex-auto">
                      <InputText
                        v-model="bidAmount"
                        placeholder="Your bid"
                        class=""
                        style="width: 100%"
                        type="text"
                      />
                    </div>
                    <div class="flex flex-auto">
                      <Button
                        label="Auto Bid"
                        class="item-btn small btn-darkblue"
                        rounded
                        @click="placeBid('AutoBid')"
                      />
                    </div>
                    <div class="flex flex-auto">
                      <Button
                        label="Direct Bid"
                        class="item-btn small btn-lightblue"
                        rounded
                        @click="placeBid('DirectBid')"
                      />
                    </div>
                  </div>
                </div>
                <div v-if="canShowBuyNow" class="buynowitem">
                  <Divider />
                  <div class="flex m-2">
                    <div class="flex">
                      <span class="pt-1 pr-1">
                        Or Buy Now for
                        <PriceDisplay
                          :price="reactiveItem.buyNowPrice"
                          :currency="reactiveItem.currency"
                        />
                      </span>
                      <Button
                        label="Buy Now"
                        class="item-btn small btn-gold"
                        rounded
                        @click="placeBid('BuyNow')"
                      />
                    </div>
                  </div>
                </div>
                <div class="grid grid-nogutter">
                  <div class="col-12">
                    <p
                      v-if="errorMessage"
                      class="text-red-500 text-sm pb-0 mb-0"
                    >
                      {{ errorMessage }}
                    </p>
                  </div>
                </div>
                <div class="flex justify-content-start align-content-center">
                  <div
                    class="flex align-items-center justify-content-start p-1"
                  >
                    <i
                      class="pi pi-info-circle"
                      style="font-size: 1rem; color: var(--gray-800)"
                    ></i>
                  </div>
                  <div
                    class="flex align-items-center justify-content-start flex-grow-1"
                  >
                    <small
                      class="m-1"
                      style="
                        color: var(--gray-800);
                        font-size: 0.75rem;
                        text-align: left;
                      "
                    >
                      <strong>Auto bid</strong> automatically place new bids for
                      you, up to your maximum amount.<br />
                      <strong>Direct bid</strong> are posted immediately without
                      waiting for other bids to be raised.
                    </small>
                  </div>
                </div>
              </div>
            </div>
            <div
              class="block md:hidden"
              style="width: 100%"
              v-if="!isBidding && !hasAuctionEnded"
            >
              <button
                class="btn btn-primary bid-action"
                style="width: 100%"
                type="button"
                @click="isOpen = true"
              >
                Place Bid
              </button>
            </div>
            <div v-else-if="hasAuctionEnded" class="flex">
              <p>Bidding has ended.</p>
            </div>
          </div>
        </div>
      </div>
    </template>
  </Card>
  <!-- Popup for Highest Bidder -->
  <div v-if="showHighestBidderPopup" class="modal-overlay">
    <div class="modal-content">
      <h3
        style="font-family: Playfair Display, serif;
    font-weight: 400;
    font-size: 1.9rem;
    text-transform: uppercase;
}"
      >
        Congratulations!
      </h3>
      <p>
        <span
          class="p-chip-icon pi pi-star-fill"
          data-pc-section="icon"
          style="color: #f0ad4e"
        ></span>
        You are the highest bidder!
        <span
          class="p-chip-icon pi pi-star-fill"
          data-pc-section="icon"
          style="color: #f0ad4e"
        ></span>
      </p>
      <div class="d-flex gap-2 justify-content-center flex-wrap">
        <Button
          label="Close"
          @click="showHighestBidderPopup = false; if (isKiosk) showKioskSignOutPrompt = true;"
          style="background: #1b2750"
        />
        <Button
          v-if="isKiosk"
          label="Sign out"
          @click="showHighestBidderPopup = false; kioskSwitchUser();"
          class="p-button-outlined"
        />
      </div>
    </div>
  </div>
  <!-- Kiosk: Sign out prompt after bid -->
  <div v-if="showKioskSignOutPrompt" class="modal-overlay">
    <div class="modal-content">
      <h3 style="font-family: Playfair Display, serif; font-weight: 400; font-size: 1.5rem;">Done bidding?</h3>
      <p>Would you like to sign out so the next person can use this device?</p>
      <div class="d-flex gap-2 justify-content-center flex-wrap">
        <Button
          label="Stay signed in"
          @click="showKioskSignOutPrompt = false"
          style="background: #1b2750"
        />
        <Button
          label="Sign out"
          @click="showKioskSignOutPrompt = false; kioskSwitchUser();"
          class="p-button-outlined"
        />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.item-btn {
  border-radius: 6px;
  margin-left: 5px;
}
.item-btn.small {
  font-size: 0.75rem;
}
.scalein {
  animation: scalein 1s forwards;
}

.animation-duration-1000 {
  animation-duration: 1s;
}

@keyframes scalein {
  from {
    transform: scale(0);
  }
  to {
    transform: scale(1);
  }
}

.text-red-500 {
  color: red;
}
.countdowntext {
  color: #000000;
}

:deep(.modal-style) {
  box-sizing: border-box;
  width: 100%;
  color: #333;
  background-color: #ffffff;
  border-radius: 1rem 1rem 0 0;

  @media (prefers-color-scheme: light) {
    color: black;
    background-color: #f7f2fa;
    box-shadow: 0 1px 4px 0 rgb(0 0 0 / 37%);
  }
}
.price-display {
  border-radius: 5px;
  padding: 3px;
  padding-left: 5px;
  background-color: #f1f5f9;
  color: #000000;
  text-align: left;
  margin-bottom: 2px;
}

.btn-gold {
  background-color: #c39935;
  border-color: #c39935;
}
.btn-green {
  background-color: #009036;
  border-color: #009036;
}
.btn-darkblue {
  background-color: #1b2750;
  border-color: #1b2750;
}
.btn-lightblue {
  background-color: #00a1e4;
  border-color: #00a1e4;
}
.bid-action {
  background: #1b2750;
}
.bid-action:hover {
  background: #1b2750;
  border-color: #1b2750;
}
.bid-action:focus {
  background: #1b2750;
  border-color: #1b2750;
}
.highest-bid-popup {
  position: fixed;
  top: 20%;
  left: 50%;
  transform: translate(-50%, -20%);
  background-color: #fff;
  padding: 20px;
  border: 1px solid #ccc;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  z-index: 1000;
}

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.modal-content {
  background: #fff;
  padding: 20px;
  margin: 20px;
  border-radius: 8px;
  max-width: 500px;
  text-align: center;
}
</style>
